import { formatDistanceToNow } from 'date-fns';

/**
 * Accepted input shape for the FE-wide datetime helpers. ISO strings are
 * the wire format; `Date` is accepted for callers that already parsed; the
 * nullish/`unknown` cases are tolerated so consumers do not need to guard
 * around partial backend payloads (helpers return the supplied `fallback`
 * instead of throwing).
 */
export type DateInput = Date | string | number | null | undefined | unknown;

/**
 * Backend instants are recorded in UTC, but some endpoints serialize them
 * WITHOUT a timezone designator (e.g. a Postgres `timestamp` column rendered
 * as `"2026-06-21T07:00:00.000"` or `"2026-06-21 07:00:00"`). `new Date(...)`
 * reads such timezone-less strings as the VIEWER'S LOCAL time, silently
 * shifting the instant by their UTC offset — a GMT+3 user sees a just-created
 * row as "about 3 hours ago" and its tooltip three hours off. Normalize these
 * to UTC by appending a `Z`.
 *
 * Left untouched: already-zoned strings (trailing `Z` or `±HH:MM`), date-only
 * strings (`"2026-06-21"`, already parsed as UTC midnight by the spec), and
 * anything without a recognizable `date + time` shape (epoch strings are
 * handled by the numeric branch before this helper is reached).
 */
function assumeUtcForZonelessDateTime(value: string): string {
  const trimmed = value.trim();
  const hasTimeComponent = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(trimmed);
  const hasTimezone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(trimmed);
  if (!hasTimeComponent || hasTimezone) {
    return value;
  }
  // Use a `T` separator so the appended `Z` yields a spec-compliant ISO string.
  return `${trimmed.replace(' ', 'T')}Z`;
}

/**
 * Coerce `DateInput` into a valid `Date` or `null`. Centralized so every
 * formatter agrees on what counts as "unparseable" (NaN time after `new
 * Date(value)`). Accepts numeric strings (epoch millis) so `formatRelativeTime`'s
 * existing behavior carries over.
 *
 * @param assumeUtc — When `true`, timezone-less datetime strings are read as
 *   UTC instead of the viewer's local time (see
 *   {@link assumeUtcForZonelessDateTime}). Callers rendering backend-recorded
 *   UTC instants pass `true`; the filter-boundary local renderer keeps `false`.
 */
function coerceToDate(value: DateInput, assumeUtc = false): Date | null {
  if (value === null || value === undefined) return null;
  let d: Date;
  if (value instanceof Date) {
    d = value;
  } else if (typeof value === 'number') {
    d = new Date(value);
  } else if (typeof value === 'string') {
    if (/^\d+$/.test(value)) {
      d = new Date(parseInt(value, 10));
    } else {
      d = new Date(assumeUtc ? assumeUtcForZonelessDateTime(value) : value);
    }
  } else {
    return null;
  }
  return Number.isNaN(d.getTime()) ? null : d;
}

// All FE date rendering is pinned to UTC with an explicit " UTC" suffix on
// the timestamp form. This is non-negotiable for backend-recorded events
// (audit logs, mute/exclude metadata, etc.): without an explicit timezone
// label, viewers in non-UTC zones — most users — would silently misinterpret
// the time as local. Calendar-only dates omit the suffix because a bare
// date already represents the same day for every viewer, and the trailing
// " UTC" reads strangely on a date with no clock.
const utcDateTimeFormatter = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'long',
  timeStyle: 'medium',
  timeZone: 'UTC',
  hour12: false,
});

const utcDateFormatter = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'long',
  timeZone: 'UTC',
});

// Long-style local renderer for filter-boundary wall-clock values. No
// `timeZone`, so a zone-less value renders the exact time the user picked;
// `hour12: false` keeps it 24-hour to match the UTC form's clock.
const absoluteDateTimeFormatter = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'long',
  timeStyle: 'medium',
  hour12: false,
});

/**
 * Format a backend-recorded instant as a long date + medium time in UTC,
 * with an explicit " UTC" suffix. The default FE renderer for any place a
 * timestamp is surfaced without the user choosing a timezone.
 *
 * @param value — ISO string, `Date`, epoch millis, or nullish.
 * @param fallback — Returned for nullish or unparseable input. Default `'—'`.
 */
export function formatUtcDateTime(value: DateInput, fallback = '—'): string {
  const d = coerceToDate(value, true);
  if (!d) return fallback;
  return `${utcDateTimeFormatter.format(d)} UTC`;
}

/**
 * Parse a backend-recorded instant into a `Date`, reading timezone-less
 * strings as UTC (see {@link assumeUtcForZonelessDateTime}) — the SAME
 * normalization the UTC formatters apply. Returns `null` for unparseable
 * input. Use this whenever you need the numeric instant behind a value you
 * also render via the UTC/relative formatters, so date math and the rendered
 * label can't disagree on a zoneless string (e.g. a freshness-tier dot vs its
 * "… ago" label).
 */
export function coerceToUtcDate(value: DateInput): Date | null {
  return coerceToDate(value, true);
}

/**
 * Format a backend-recorded instant as a long calendar date in UTC. No
 * clock component, no " UTC" suffix (see comment above).
 */
export function formatUtcDate(value: DateInput, fallback = '—'): string {
  const d = coerceToDate(value, true);
  if (!d) return fallback;
  return utcDateFormatter.format(d);
}

/**
 * Format a date/time as a long-style, 24-hour local timestamp (e.g.
 * `January 15, 2024 at 14:30:00`). No `timeZone`, so a zone-less wall-clock
 * value (`yyyy-MM-dd'T'HH:mm:ss`, the shape filter boundaries are stored in)
 * renders exactly the time the user picked — no UTC shift. Backs only
 * `FormattedTime` for filter-boundary local rendering.
 *
 * @param value — ISO string, `Date`, epoch millis, or nullish.
 * @param fallback — Returned for nullish or unparseable input. Default `'—'`.
 */
export function formatAbsoluteDateTime(
  value: DateInput,
  fallback = '—'
): string {
  const d = coerceToDate(value);
  if (!d) return fallback;
  return absoluteDateTimeFormatter.format(d);
}

/**
 * Coerce relative-time input into a valid `Date`, or `null` when it cannot be
 * parsed. Shared prologue for `formatRelativeTime` and
 * `formatRelativeTimeFuzzy` so both agree on what counts as valid.
 *
 * NOTE: these relative helpers have their own legacy parsing rules — in
 * particular they reject raw `number` input (their callers have never passed
 * epoch millis, and several tests pin that contract). They therefore do *not*
 * delegate to `coerceToDate`, which intentionally accepts numbers for the UTC
 * formatters.
 */
function parseRelativeDate(
  date: Date | string | undefined | null | unknown
): Date | null {
  if (date === undefined || date === null) {
    return null;
  }

  let dateObj: Date;
  if (typeof date === 'string') {
    if (/^\d+$/.test(date)) {
      dateObj = new Date(parseInt(date, 10));
    } else {
      // Backend instants are UTC; read timezone-less strings as UTC so the
      // delta against `new Date()` (also an absolute instant) is correct for
      // viewers in any timezone — not shifted by their local offset.
      dateObj = new Date(assumeUtcForZonelessDateTime(date));
    }
  } else if (date instanceof Date) {
    dateObj = date;
  } else {
    return null;
  }

  if (isNaN(dateObj.getTime())) {
    return null;
  }

  return dateObj;
}

/**
 * Format a date as a relative time string with minute precision (the default
 * renderer for all screens whose timestamps are accurate to the minute).
 * - < 1 minute ago: "just now"
 * - 1–59 minutes ago: exact minutes (e.g. "1 minute ago", "37 minutes ago")
 * - 1–23 hours ago: date-fns "about X hour(s) ago"
 * - >= 1 day / future: date-fns ("5 days ago", "about 2 weeks ago", "in 2 hours")
 *
 * @param date - The date to format. Can be a Date object, ISO string, or other date-parseable value.
 * @returns A human-readable relative time string, or "invalid" if the date is invalid or missing
 */
export function formatRelativeTime(
  date: Date | string | undefined | null | unknown
): string {
  const dateObj = parseRelativeDate(date);
  if (!dateObj) {
    return 'invalid';
  }

  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();

  // If the date is in the future (diffMs is negative) or 1 day or more in the past,
  // delegate to formatDistanceToNow which handles both future and distant past dates
  if (diffMs < 0 || diffMs >= 24 * 60 * 60 * 1000) {
    return formatDistanceToNow(dateObj, { addSuffix: true });
  }

  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  // Less than 1 minute: "just now"
  if (diffMinutes < 1) {
    return 'just now';
  }

  // 1–59 minutes: exact minutes
  if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  }

  // 1–23 hours: lean on date-fns for the "about X hour(s) ago" wording
  return formatDistanceToNow(dateObj, { addSuffix: true });
}

/**
 * Fuzzy variant of {@link formatRelativeTime} for the one screen whose data is
 * not accurate to the minute (Inventory → Users → "Kirin Last Active"). Never
 * surfaces an exact minute count below the hour:
 * - < 15 minutes ago: "less than 15 minutes ago"
 * - 15–59 minutes ago: "less than 1 hour ago"
 * - 1–23 hours ago: date-fns "about X hour(s) ago"
 * - >= 1 day / future: same delegation as {@link formatRelativeTime}
 *
 * @param date - The date to format. Can be a Date object, ISO string, or other date-parseable value.
 * @returns A human-readable relative time string, or "invalid" if the date is invalid or missing
 */
export function formatRelativeTimeFuzzy(
  date: Date | string | undefined | null | unknown
): string {
  const dateObj = parseRelativeDate(date);
  if (!dateObj) {
    return 'invalid';
  }

  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();

  // Future or 1 day or more in the past: identical delegation to the default.
  if (diffMs < 0 || diffMs >= 24 * 60 * 60 * 1000) {
    return formatDistanceToNow(dateObj, { addSuffix: true });
  }

  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  // Less than 15 minutes: fuzzy lower bound.
  if (diffMinutes < 15) {
    return 'less than 15 minutes ago';
  }

  // 15–59 minutes: never an exact count, collapse to "less than 1 hour ago".
  if (diffMinutes < 60) {
    return 'less than 1 hour ago';
  }

  // 1–23 hours: same "about X hour(s) ago" wording as the default.
  return formatDistanceToNow(dateObj, { addSuffix: true });
}

/**
 * Returns the end of `d` (23:59:59.999) in UTC as an ISO 8601 string.
 *
 * Used to construct mute-alert expirations from a user-picked calendar day:
 * the user picks a day, and the mute is honored up to the very end of that
 * day in UTC. Always emits a Z-terminated ISO string so it round-trips
 * through Zod's `z.string().datetime()` validator on the wire.
 *
 * Date components are read in UTC to avoid local-timezone DST drift.
 *
 * @param d - Any Date instance (its time component is ignored)
 * @returns ISO 8601 datetime string at UTC end-of-day for `d`
 */
export function endOfDayUTC(d: Date): string {
  return new Date(
    Date.UTC(
      d.getUTCFullYear(),
      d.getUTCMonth(),
      d.getUTCDate(),
      23,
      59,
      59,
      999
    )
  ).toISOString();
}

/**
 * Format an absolute date as a locale string. Returns "—" for null/undefined.
 * Used for "Created" / "Last used" / "Expires" cells in admin tables.
 */
export function formatAbsoluteDate(
  value: Date | string | null | undefined
): string {
  if (value === null || value === undefined) return '—';
  return new Date(value).toLocaleString();
}
